<script setup>
import {ref, inject, computed, watch, nextTick, onMounted} from "vue";
import needsStartupsService from "@/services/needs-startups.service";
import MatchMakingModalCard from "@/views/needs/need/matchMaking/MatchMakingModalCard.vue";
import {useRoute} from "vue2-helpers/vue-router";

const {source} = defineProps(['source']);
const emit = defineEmits(['updated'])

const unreviewedStartups = inject('unreviewedStartups');
const showModal = ref(false);
const startups = ref([]);
const index = ref(0);
const flying = ref(false);
const flyDir = ref('');
const flyingId = ref(null);
const topCardRef = ref(null);
const nearCardRef = ref(null);
const startupCard = ref(null);

const route = useRoute();
const startup = computed(() => startups.value[index.value]);
const nextStartup = computed(() => startups.value[index.value + 1]);

watch(showModal, (value) => {
    if (!value) {
        emit('updated');
    }
});

onMounted(() => {
    if (route.query.openMatchMaking && route.query.openMatchMaking === source) {
        open();
    }
})

function skip() {
    if (index.value === startups.value.length - 1) {
        index.value = 0;
    } else {
        ++index.value;
    }
}

async function rate(result) {
    await needsStartupsService.setStartupMatchStatus(startup.value.link_id, result);
    startups.value.splice(index.value, 1);

    if (startups.value && index.value >= startups.value.length) {
        index.value = 0;
    }
}

async function flyOff(dir, result) {
    if (flying.value) return;

    if (startupCard.value?.showDetails !== undefined) {
        startupCard.value.showDetails = false;
    }

    flyDir.value = dir;
    flyingId.value = startup.value.id;
    flying.value = true;

    // await new Promise(r => setTimeout(r, 600));
    await new Promise(r => setTimeout(r, 1000));
    if (topCardRef.value) {
        topCardRef.value.style.transition = 'none';
        void topCardRef.value.offsetHeight;
        topCardRef.value.style.transform = 'translateX(0)';
        topCardRef.value.style.opacity = '1';
    }
    if (nearCardRef.value) {
        nearCardRef.value.style.transition = 'none';
    }

    await rate(result);

    flyingId.value = null;
    flying.value = false;

    await nextTick();

    if (topCardRef.value) {
        topCardRef.value.style.transition = '';
        topCardRef.value.style.transform = '';
        topCardRef.value.style.opacity = '';
    }
    if (nearCardRef.value) {
        nearCardRef.value.style.transition = '';
    }
}

function open() {
    startups.value = unreviewedStartups.value.filter(item => item.source === source);
    showModal.value = true;
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer modal-class="modal-matchmaking">
        <template v-if="startups.length">
            <div
                class="modal-matchmaking__icon-wrapper"
                @click="flyOff('left', 'disliked')"
            >
                <div class="modal-matchmaking__close"></div>
            </div>

            <div class="modal-matchmaking__wrapper">
                <div class="modal-matchmaking__set" :class="{'is-swiping': flying}">
                    <div
                        class="modal-matchmaking__content"
                        :style="{ visibility: startups.length > 2 ? 'visible' : 'hidden' }"
                    />
                    <div
                        ref="nearCardRef"
                        class="modal-matchmaking__content"
                        :style="{ visibility: startups.length > 1 ? 'visible' : 'hidden' }"
                    >
                        <MatchMakingModalCard
                            v-if="nextStartup"
                            :startup="nextStartup"
                            :source="source"
                            dummy
                        />
                    </div>

                    <div ref="topCardRef" class="modal-matchmaking__content"
                         :class="{ 'modal-matchmaking__content--expanded': startupCard?.showDetails, 'is-flying-left': flyingId === startup.id && flyDir === 'left', 'is-flying-right': flyingId === startup.id && flyDir === 'right' }">

                        <MatchMakingModalCard
                            ref="startupCard"
                            :startup="startup"
                            :source="source"
                        />
                    </div>
                </div>

                <div class="modal-matchmaking__bottom">
                    <a
                        class="modal-matchmaking__skip"
                        @click="skip"
                    >
                        Skip
                    </a>
                </div>
            </div>

            <div
                class="modal-matchmaking__icon-wrapper"
                @click="flyOff('right', 'liked')"
            >
                <div class="modal-matchmaking__love"></div>
            </div>
        </template>

        <template v-else>
            <div class="modal-matchmaking__all-reviewed">
                <p class="modal-text">You've reviewed all your matches</p>

                <button class="button" @click="showModal = false">Back To Need</button>
            </div>
        </template>
    </b-modal>
</template>

<style scoped>
    .is-flying-left {
        transform: translateX(-150vw) rotate(-20deg) !important;
        opacity: 0 !important;
    }

    .is-flying-right {
        transform: translateX(150vw) rotate(20deg) !important;
        opacity: 0 !important;
    }

    .is-swiping .modal-matchmaking__content:nth-child(2) {
        transform: translateY(0) !important;
        transition: transform 0.6s ease !important;
    }

    .is-flying-left,
    .is-flying-right {
        transition: transform 0.6s ease, opacity 0.6s ease !important;
    }
    .modal-matchmaking__content:last-of-type {
        transition: transform 0.6s ease, opacity 0.6s ease, max-width 0.2s ease;
    }
</style>
